import { useState, useEffect, useRef } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Toast from '../components/ui/Toast';
import { fetchWithAdminAuth } from '../utils/adminAuth';

interface Settings {
  [key: string]: string;
}

export default function Config() {
  const [settings, setSettings] = useState<Settings>({
    iva_rate: '23',
    currency: 'EUR',
    max_session_duration: '20',
    balance_end_message: 'O seu saldo terminou. Pode carregar novamente para continuar a consulta.',
    min_balance_warning: '5',
    session_timeout: '30',
    site_name: 'Tarot Virtual',
    ai_system_prompt: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const savedSettingsRef = useRef<Settings>({});

  useEffect(() => {
    fetchSettings();
  }, []);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAdminAuth('/api/admin/settings');
      if (response.ok) {
        const data = await response.json();
        const merged = { ...settings, ...data };
        setSettings(merged);
        savedSettingsRef.current = { ...merged };
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await fetchWithAdminAuth('/api/admin/settings/bulk', {
        method: 'PUT',
        body: JSON.stringify(settings),
      });
      
      if (response.ok) {
        savedSettingsRef.current = { ...settings };
        setHasUnsavedChanges(false);
        setToast({ message: 'Configurações guardadas com sucesso!', type: 'success' });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setToast({ message: 'Erro ao guardar configurações', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key: string, value: string) => {
    setSettings(prev => {
      const updated = { ...prev, [key]: value };
      // Check if anything differs from saved
      const isDirty = Object.keys(updated).some(k => updated[k] !== savedSettingsRef.current[k]);
      setHasUnsavedChanges(isDirty);
      return updated;
    });
  };

  const inputClass = "w-full bg-[rgba(10,11,20,0.9)] rounded-lg border border-border-soft px-2 py-1.5 text-text text-[0.78rem] outline-none focus:border-accent/50 transition-colors";

  if (loading) {
    return (
      <div className="px-3 sm:px-6 py-4 sm:py-5">
        <Card title="Configurações gerais" subtitle="A carregar...">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-5">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onClose={() => setToast(null)}
        />
      )}
      
      <Card
        title="Configurações gerais"
        subtitle="Parâmetros do sistema, IVA, limites de sessão e comportamento do chat IA."
        action={
          <div className="flex items-center gap-3">
            {hasUnsavedChanges && (
              <span className="text-[0.72rem] text-yellow-400 flex items-center gap-1.5 animate-pulse">
                <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                Alterações não guardadas
              </span>
            )}
            <Button variant="primary" onClick={handleSave} disabled={saving || !hasUnsavedChanges}>
              {saving ? 'A guardar...' : 'Guardar alterações'}
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <h3 className="text-[0.85rem] mb-1.5">Geral</h3>
            <label className="text-[0.78rem] block mb-1">Nome do site</label>
            <input 
              className={inputClass}
              value={settings.site_name}
              onChange={(e) => handleChange('site_name', e.target.value)}
            />

            <h3 className="text-[0.85rem] mt-4 mb-1.5">Fiscalidade</h3>
            <label className="text-[0.78rem] block mb-1">Taxa de IVA principal (%)</label>
            <input 
              className={inputClass}
              value={settings.iva_rate}
              onChange={(e) => handleChange('iva_rate', e.target.value)}
            />
            <label className="text-[0.78rem] block mt-2.5 mb-1">Moeda</label>
            <input 
              className={inputClass}
              value={settings.currency}
              onChange={(e) => handleChange('currency', e.target.value)}
            />
            <label className="text-[0.78rem] block mt-2.5 mb-1">Saldo mínimo para aviso (€)</label>
            <input 
              className={inputClass}
              value={settings.min_balance_warning}
              onChange={(e) => handleChange('min_balance_warning', e.target.value)}
            />
          </div>
          <div>
            <h3 className="text-[0.85rem] mb-1.5">Sessões IA</h3>
            <label className="text-[0.78rem] block mb-1">Duração máxima por sessão IA (minutos)</label>
            <input 
              className={inputClass}
              value={settings.max_session_duration}
              onChange={(e) => handleChange('max_session_duration', e.target.value)}
            />
            <label className="text-[0.78rem] block mt-2.5 mb-1">Timeout de sessão (minutos)</label>
            <input 
              className={inputClass}
              value={settings.session_timeout}
              onChange={(e) => handleChange('session_timeout', e.target.value)}
            />
            <label className="text-[0.78rem] block mt-2.5 mb-1">Mensagem quando saldo termina</label>
            <textarea 
              className={`${inputClass} min-h-[70px] resize-vertical`}
              value={settings.balance_end_message}
              onChange={(e) => handleChange('balance_end_message', e.target.value)}
            />
            <label className="text-[0.78rem] block mt-2.5 mb-1">System prompt da IA (personalidade do consultor)</label>
            <textarea 
              className={`${inputClass} min-h-[90px] resize-vertical`}
              value={settings.ai_system_prompt}
              onChange={(e) => handleChange('ai_system_prompt', e.target.value)}
              placeholder="Ex: Tu és um consultor de tarot especializado..."
            />
          </div>
        </div>
      </Card>
    </div>
  );
}
